<template>
  <div>
    <Movementmenu_govem />

    <v-container fluid>
      <!-- Header Section with Statistics -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="pa-4" elevation="4">
            <v-card-title class="primary--text">
              <v-icon large color="primary" class="mr-3">mdi-school</v-icon>
              ข้อมูลสถานศึกษารายงาน สาขาวิชาสำหรับตำแหน่งว่าง
            </v-card-title>          
          </v-card>
        </v-col>
      </v-row>

      <!-- Statistics Cards -->
      <v-row class="mb-6">
        <v-col
          cols="12"
          sm="6"
          md="3"
          v-for="stat in statisticsCards"
          :key="stat.title"
        >
          <v-card
            :color="stat.color"
            dark
            class="text-center pa-4 stat-card"
            elevation="6"
            @click="filterByStatus(stat.filter)"
            :class="{ 'stat-card--active': statusFilter === stat.filter }"
          >
            <div class="stat-icon-container">
              <v-icon size="48" class="mb-3">{{ stat.icon }}</v-icon>
            </div>
            <h1 class="font-weight-bold mb-2">{{ stat.value }}</h1>
            <p class="mb-0">{{ stat.title }}</p>
            <v-progress-linear
              v-if="stat.percentage"
              :value="stat.percentage"
              color="white"
              background-color="rgba(255,255,255,0.3)"
              height="6"
              class="mt-2"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- Analytics Dashboard -->
      <v-row class="mb-6">
        <!-- Status Distribution Chart -->
        <v-col cols="12" lg="6">
          <v-card elevation="4" class="chart-card">
            <v-card-title>
              <v-icon class="mr-2">mdi-chart-pie</v-icon>
              การกระจายสถานะรายงาน
            </v-card-title>
            <v-card-text>
              <apexchart
                type="pie"
                :options="statusPieOptions"
                :series="statusPieSeries"
                height="300"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Regional Distribution Chart -->
        <v-col cols="12" lg="6">
          <v-card elevation="4" class="chart-card">
            <v-card-title>
              <v-icon class="mr-2">mdi-map-marker-multiple</v-icon>
              การกระจายตามภูมิภาค
            </v-card-title>
            <v-card-text>
              <apexchart
                type="bar"
                :options="regionBarOptions"
                :series="regionBarSeries"
                height="300"
              />
            </v-card-text>
          </v-card>
        </v-col>

  
      </v-row>

      <!-- Main Data Table -->
      <base-material-card
        icon="mdi-table"
        title="ตารางข้อมูลรายงาน"
        class="px-5 py-3"
        elevation="4"
      >
        <!-- Filter and Action Bar -->
        <v-card flat class="mb-4">
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="ค้นหา ระบุคำ หรือ ส่วนข้อความ"
                  single-line
                  hide-details
                  dense
                  filled
                  clearable
                  class="search-field"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="statusFilter"
                  :items="statusFilterItems"
                  label="กรองตามสถานะ"
                  clearable
                  dense
                  filled
                  prepend-inner-icon="mdi-filter"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="regionFilter"
                  :items="regionFilterItems"
                  label="กรองตามภูมิภาค"
                  clearable
                  dense
                  filled
                  prepend-inner-icon="mdi-map"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-btn
                  color="primary"
                  block
                  @click="exportData"
                  :loading="exportLoading"
                  class="export-btn"
                >
                  <v-icon left>mdi-download</v-icon>
                  Export Excel
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Column Selection -->
        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-icon class="mr-2">mdi-view-column</v-icon>
              ปรับแต่งคอลัมน์การแสดงผล
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-select
                v-model="selectedHeaders"
                :items="headers"
                label="เลือกคอลัมน์ที่ต้องการแสดง"
                multiple
                outlined
                return-object
                chips
                small-chips
                deletable-chips
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 4"
                    small
                    close
                    @click:close="removeHeader(item)"
                    :color="index % 2 === 0 ? 'primary' : 'secondary'"
                  >
                    {{ item.text }}
                  </v-chip>
                  <span v-if="index === 4" class="grey--text caption">
                    (+{{ selectedHeaders.length - 4 }} คอลัมน์)
                  </span>
                </template>
              </v-select>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Data Table -->
        <v-data-table
          :headers="showHeaders"
          :items="filteredReports"
          :search="search"
          :loading="loading"
          class="elevation-2 modern-table"
          :items-per-page="itemsPerPage"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
            'items-per-page-text': 'แถวต่อหน้า:'
          }"
          show-select
          v-model="selectedItems"
        >
          <template v-slot:loading>
            <div class="text-center pa-6">
              <v-progress-circular indeterminate color="primary" size="60" />
              <p class="mt-4">กำลังโหลดข้อมูล...</p>
            </div>
          </template>

          <!-- File Column -->
          <template v-slot:item.college_manpower_reportfile="{ item }">
            <div class="text-center">
                           <v-btn
                    v-if="item.college_manpower_reportfile != '-'"
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="error"
                    @click="viewpdf(item.college_manpower_reportfile)"
                    class="pdf-btn"
                  >
                    <v-icon size="32">mdi-file-pdf-box</v-icon>
                  </v-btn>
                  <v-icon v-else color="grey" size="32"
                    >mdi-file-outline</v-icon
                  >
                           
          
            </div>
          </template>

          <!-- College Name -->
          <template v-slot:item.college_manpower_reportcollege="{ item }">
            <div class="college-name">
              <v-chip small color="primary" outlined class="mb-1">
                {{ item.college_manpower_reportcollege }}
              </v-chip>
              <div class="grey--text">
                {{ item.user_firstname }}
              </div>
            </div>
          </template>

          <!-- Province and Region -->
          <template v-slot:item.province_name="{ item }">
            <div class="location-info">
              <div class="font-weight-medium">
                {{ item.province_name || "ไม่ระบุ" }}
              </div>
              <v-chip
                x-small
                :color="getRegionColor(item.region_name)"
                dark
                class="mt-1"
              >
                {{ getRegionDisplayName(item.region_name) }}
              </v-chip>
            </div>
          </template>

          <!-- Position Count -->
          <template v-slot:item.college_manpower_reportcountpos="{ item }">
            <div class="text-center">
              <v-chip
                :color="getCountColor(item.college_manpower_reportcountpos)"
                dark
                small
              >
                {{ item.college_manpower_reportcountpos }} ตำแหน่ง
              </v-chip>
            </div>
          </template>

          <!-- Status -->
          <template v-slot:item.college_manpower_reportfilestatus="{ item }">
            <div class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.college_manpower_reportfilestatus === 'cc'"
                    v-bind="attrs"
                    v-on="on"
                    small
                    fab
                    color="error"
                    @click="cancelAlert(item.college_manpower_reportref)"
                    :loading="cancelLoading === item.college_manpower_reportref"
                  >
                    <v-icon>mdi-alert</v-icon>
                  </v-btn>
                  <v-icon
                    v-else-if="item.college_manpower_reportfilestatus === 'cf'"
                    v-bind="attrs"
                    v-on="on"
                    color="success"
                    size="36"
                  >
                    mdi-check-circle
                  </v-icon>
                  <v-icon
                    v-else
                    v-bind="attrs"
                    v-on="on"
                    size="36"
                    color="warning"
                  >
                    mdi-progress-tag
                  </v-icon>
                </template>
                <span>{{
                  getStatusText(item.college_manpower_reportfilestatus)
                }}</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Timestamp -->
          <template v-slot:item.college_manpower_reporttimestamp="{ item }">
            <div class="timestamp-col">
              <div class="font-weight-medium">
                {{ formatDate(item.college_manpower_reporttimestamp) }}
              </div>
              <div class="grey--text">
                {{ formatTime(item.college_manpower_reporttimestamp) }}
              </div>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:item.del_action="{ item }">
            <div class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    @click="delReport(item.college_manpower_reportref)"
                    color="error"
                    small
                    fab
                    :loading="deleteLoading === item.college_manpower_reportref"
                    class="delete-btn"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>ลบรายการทั้งหมด</span>
              </v-tooltip>
            </div>
          </template>

          <!-- No Results -->
          <template v-slot:no-results>
            <div class="text-center pa-6">
              <v-icon size="80" color="grey lighten-2"
                >mdi-database-search</v-icon
              >
              <p class="mt-4 grey--text">ไม่พบผลลัพธ์ "{{ search }}"</p>
              <v-btn color="primary" outlined @click="search = ''">
                ล้างการค้นหา
              </v-btn>
            </div>
          </template>

          <!-- No Data -->
          <template v-slot:no-data>
            <div class="text-center pa-6">
              <v-icon size="80" color="grey lighten-2">mdi-database-off</v-icon>
              <p class="mt-4 grey--text">ไม่มีข้อมูล</p>
            </div>
          </template>
        </v-data-table>

        <!-- Bulk Actions -->
        <v-card flat v-if="selectedItems.length > 0" class="mt-4">
          <v-card-text>
            <v-alert type="info" dense>
              เลือกแล้ว {{ selectedItems.length }} รายการ
              <v-btn
                color="error"
                small
                outlined
                class="ml-2"
                @click="bulkDelete"
                :loading="bulkDeleteLoading"
              >
                <v-icon left small>mdi-delete</v-icon>
                ลบที่เลือก
              </v-btn>
            </v-alert>
          </v-card-text>
        </v-card>
      </base-material-card>

      <!-- PDF Viewer Dialog -->
      <v-dialog v-model="viewDialog" max-width="90%" max-height="90%">
        <v-card>
          <v-card-title>
            <span class="text-h6">ดูไฟล์ PDF</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="viewDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <embed
              :src="'/HRvecCollegeIdPos/' + viewfile"
              width="100%"
              height="700px"
              type="application/pdf"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Movementmenu_govem from "../../components/admin/movementmenu_govem.vue";

export default {
  name: "CollegeReportDashboard",
  components: { Movementmenu_govem },

  data() {
    return {
      loading: true,
      exportLoading: false,
      cancelLoading: null,
      deleteLoading: null,
      bulkDeleteLoading: false,
      ApiKey: "HRvec2021",
      search: "",
      statusFilter: null,
      regionFilter: null,
      itemsPerPage: 25,
      selectedItems: [],

      headers: [],
      selectedHeaders: [],

      headersMap: [
        {
          text: "ID อ้างอิง",
          align: "left",
          value: "college_manpower_reportref"
        },
        {
          text: "สถานศึกษา",
          align: "left",
          value: "college_manpower_reportcollege"
        },
        { text: "ผู้รายงาน", align: "left", value: "user_firstname" },
        { text: "จังหวัด", align: "left", value: "province_name" },
        { text: "ภูมิภาค", align: "center", value: "region_name" },
        {
          text: "จำนวนตำแหน่ง",
          align: "center",
          value: "college_manpower_reportcountpos"
        },
        {
          text: "ไฟล์รายงาน",
          align: "center",
          value: "college_manpower_reportfile"
        },
        {
          text: "สถานะ",
          align: "center",
          value: "college_manpower_reportfilestatus"
        },
        {
          text: "วันที่รายงาน",
          align: "center",
          value: "college_manpower_reporttimestamp"
        },
        {
          text: "จัดการ",
          align: "center",
          value: "del_action",
          sortable: false
        }
      ],

      college_manpower_reports: [],
      collegeReport: {
        countcc: 0,
        countcf: 0,
        countpc: 0
      },
      period_colleges: {},
      viewfile: "",
      viewDialog: false,

      // Chart Options
      statusPieOptions: {
        chart: {
          type: "pie",
          fontFamily: "Roboto, sans-serif"
        },
        labels: ["เสร็จสิ้น", "ผิดพลาด", "รอดำเนินการ"],
        colors: ["#B3151B", "#424242", "#E57373"],
        legend: {
          position: "bottom",
          fontSize: "14px"
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300
              },
              legend: {
                position: "bottom"
              }
            }
          }
        ]
      },

      regionBarOptions: {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "65%",
            endingShape: "rounded",
            borderRadius: 4,
            distributed: true,
            dataLabels: {
              position: "top"
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#B3151B"],
            fontWeight: "bold"
          },
          formatter: function(val) {
            return val + " รายงาน";
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "11px",
              fontWeight: 600,
              colors: "#263238"
            },
            rotate: -45,
            rotateAlways: true,
            maxHeight: 80
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          title: {
            text: "จำนวนรายงาน",
            style: {
              fontSize: "12px",
              fontWeight: 600,
              color: "#263238"
            }
          },
          labels: {
            style: {
              fontSize: "11px",
              colors: "#263238"
            }
          }
        },
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014", "#BDBDBD", "#212121", "#F1B0B0"],
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.25,
            gradientToColors: undefined,
            inverseColors: false,
            opacityFrom: 0.85,
            opacityTo: 0.65,
            stops: [0, 100]
          }
        },
        grid: {
          show: true,
          borderColor: "#e0e0e0",
          strokeDashArray: 3,
          position: "back",
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          enabled: true,
          theme: "dark",
          style: {
            fontSize: "12px",
            fontFamily: "Roboto, sans-serif"
          },
          y: {
            formatter: function(value) {
              return value + " รายงาน";
            }
          }
        }
      },

      trendAreaOptions: {
        chart: {
          type: "area",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: false
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "smooth",
          width: 2
        },
        xaxis: {
          categories: [
            "ม.ค.",
            "ก.พ.",
            "มี.ค.",
            "เม.ย.",
            "พ.ค.",
            "มิ.ย.",
            "ก.ค.",
            "ส.ค.",
            "ก.ย.",
            "ต.ค.",
            "พ.ย.",
            "ธ.ค."
          ]
        },
        yaxis: {
          title: {
            text: "จำนวนตำแหน่ง"
          }
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 100]
          }
        },
        colors: ["#B3151B", "#424242", "#E57373"]
      }
    };
  },

  computed: {
    showHeaders() {
      return this.headers.filter(h => this.selectedHeaders.includes(h));
    },

    filteredReports() {
      let filtered = [...this.college_manpower_reports];

      if (this.statusFilter) {
        filtered = filtered.filter(
          item => item.college_manpower_reportfilestatus === this.statusFilter
        );
      }

      if (this.regionFilter) {
        filtered = filtered.filter(
          item => item.region_name === this.regionFilter
        );
      }

      return filtered;
    },

    statisticsCards() {
      const total =
        this.collegeReport.countcf +
        this.collegeReport.countcc +
        this.collegeReport.countpc;
      return [
        {
          title: "เสร็จสิ้น",
          value: this.collegeReport.countcf,
          color: "success",
          icon: "mdi-check-circle",
          filter: "cf",
          percentage: total > 0 ? (this.collegeReport.countcf / total) * 100 : 0
        },
        {
          title: "ผิดพลาด",
          value: this.collegeReport.countcc,
          color: "error",
          icon: "mdi-alert",
          filter: "cc",
          percentage: total > 0 ? (this.collegeReport.countcc / total) * 100 : 0
        },
        {
          title: "รอดำเนินการ",
          value: this.collegeReport.countpc,
          color: "warning",
          icon: "mdi-progress-tag",
          filter: "pc",
          percentage: total > 0 ? (this.collegeReport.countpc / total) * 100 : 0
        },
        {
          title: "ทั้งหมด",
          value: total,
          color: "primary",
          icon: "mdi-format-list-numbered",
          filter: null,
          percentage: 100
        }
      ];
    },

    statusPieSeries() {
      return [
        this.collegeReport.countcf,
        this.collegeReport.countcc,
        this.collegeReport.countpc
      ];
    },

    regionBarSeries() {
      const regionData = this.getRegionData();
      return [
        {
          name: "จำนวนรายงานแต่ละภูมิภาค",
          data: regionData.values.map((value, index) => ({
            x: regionData.labels[index],
            y: value,
            fillColor: regionData.colors[index]
          }))
        }
      ];
    },

        statusFilterItems() {
      return [
        { text: "เสร็จสิ้น", value: "cf" },
        { text: "ผิดพลาด", value: "cc" },
        { text: "รอดำเนินการ", value: "pc" }
      ];
    },

    regionFilterItems() {
      const regions = [
        ...new Set(this.college_manpower_reports.map(item => item.region_name))
      ];
      return regions.map(region => ({ text: region, value: region }));
    }
  },

  async created() {
    this.headers = [...this.headersMap];
    this.selectedHeaders = [...this.headers];
  },

  async mounted() {
    await this.periodQuery();
    await this.college_manpower_reportQueryAll();
  },

  methods: {
    filterByStatus(status) {
      this.statusFilter = this.statusFilter === status ? null : status;
    },

    getRegionData() {
      const regionCounts = {};

      // นับจำนวนรายงานในแต่ละภูมิภาค
      this.college_manpower_reports.forEach(item => {
        const regionName = item.region_name || "ไม่ระบุ";
        regionCounts[regionName] = (regionCounts[regionName] || 0) + 1;
      });

      // แปลงเป็น array และเรียงลำดับ
      const regionEntries = Object.entries(regionCounts);

      // เรียงลำดับตามจำนวนรายงาน (มากไปน้อย)
      regionEntries.sort((a, b) => b[1] - a[1]);

      const labels = regionEntries.map(entry => entry[0]);
      const values = regionEntries.map(entry => entry[1]);

      // กำหนดสีสำหรับแต่ละภูมิภาค
      const regionColors = {
        ภาคเหนือ: "#455A64",
        ภาคกลาง: "#2E7D32",
        ภาคใต้: "#A86400",
        "ภาคตะวันออก กรุงเทพและปริมณฑล": "#B3151B",
        ภาคตะวันออกเฉียงเหนือ: "#F44336",
        ไม่ระบุ: "#9E9E9E"
      };

      // สร้างอาเรย์สีตามลำดับของ labels
      const colors = labels.map(label => regionColors[label] || "#9E9E9E");

      // Update chart options
      this.regionBarOptions.xaxis.categories = labels;
      this.regionBarOptions.colors = colors;

      // เพิ่มการแสดงผลที่ดีขึ้น
      this.regionBarOptions.plotOptions.bar.distributed = true;

      // เพิ่ม dataLabels สำหรับแสดงจำนวน
      this.regionBarOptions.dataLabels = {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: "12px",
          colors: ["#B3151B"],
          fontWeight: "bold"
        },
        formatter: function(val) {
          return val + " รายงาน";
        }
      };

      // เพิ่ม tooltip ที่ดีขึ้น
      this.regionBarOptions.tooltip = {
        enabled: true,
        theme: "dark",
        style: {
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        },
        y: {
          formatter: function(value) {
            return value + " รายงาน";
          }
        },
        x: {
          formatter: function(value) {
            return "ภูมิภาค: " + value;
          }
        }
      };
      return { labels, values, colors };
    },

    async viewpdf(filename) {
      this.viewfile = filename;
      this.viewDialog = true;
    },

    async delReport(reportref) {
      const result = await swal.fire({
        title: "ลบรายการ",
        text: "ลบเพื่อดำเนินการสร้างใหม่",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b3151b",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      if (result.isConfirmed) {
        this.deleteLoading = reportref;

        try {
          const reportResult = await this.$http.post(
            "college_manpower_report.php",
            {
              ApiKey: this.ApiKey,
              college_manpower_reportref: reportref
            }
          );

          const reportData = reportResult.data;
          reportData.ApiKey = this.ApiKey;

          // Delete file
          await this.$http.post("deletefile.php", {
            ApiKey: this.ApiKey,
            filename: `../HRvecfiles/${reportData.college_manpower_reportfile}`
          });

          // Delete record
          await this.$http.post(
            "college_manpower_report.delete.php",
            reportData
          );

          // Delete reference
          await this.$http.post("college_manpower.deleteidref.php", {
            ApiKey: this.ApiKey,
            college_manpower_ref: reportref
          });

          swal.fire({
            title: "ดำเนินการข้อมูลเรียบร้อย",
            icon: "success",
            showConfirmButton: false,
            timer: 1500
          });

          await this.college_manpower_reportQueryAll();
        } catch (error) {
          swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: error.message || "ไม่สามารถยกเลิกการแนบไฟล์ได้"
          });
        } finally {
          this.cancelLoading = null;
        }
      }
    },

    async bulkDelete() {
      const result = await swal.fire({
        title: `ลบรายการที่เลือก (${this.selectedItems.length} รายการ)`,
        text: "การดำเนินการนี้ไม่สามารถย้อนกลับได้",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#b3151b",
        confirmButtonText: "ยืนยันการลบ",
        cancelButtonText: "ยกเลิก"
      });

      if (result.isConfirmed) {
        this.bulkDeleteLoading = true;

        try {
          const deletePromises = this.selectedItems.map(async item => {
            const reportResult = await this.$http.post(
              "college_manpower_report.php",
              {
                ApiKey: this.ApiKey,
                college_manpower_reportref: item.college_manpower_reportref
              }
            );

            const reportData = reportResult.data;
            reportData.ApiKey = this.ApiKey;

            // Delete file
            await this.$http.post("deletefile.php", {
              ApiKey: this.ApiKey,
              filename: `../HRvecfiles/${reportData.college_manpower_reportfile}`
            });

            // Delete record
            await this.$http.post(
              "college_manpower_report.delete.php",
              reportData
            );

            // Delete reference
            await this.$http.post("college_manpower.deleteidref.php", {
              ApiKey: this.ApiKey,
              college_manpower_ref: item.college_manpower_reportref
            });
          });

          await Promise.all(deletePromises);

          swal.fire({
            title: "ดำเนินการเรียบร้อย",
            text: `ลบรายการเรียบร้อย ${this.selectedItems.length} รายการ`,
            icon: "success",
            showConfirmButton: false,
            timer: 2000
          });

          this.selectedItems = [];
          await this.college_manpower_reportQueryAll();
        } catch (error) {
          swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: "ไม่สามารถลบรายการบางรายการได้"
          });
        } finally {
          this.bulkDeleteLoading = false;
        }
      }
    },

    async exportData() {
      this.exportLoading = true;

      try {
        // Create CSV data
        const csvData = this.filteredReports.map(item => ({
          "ID อ้างอิง": item.college_manpower_reportref,
          สถานศึกษา: item.college_manpower_reportcollege,
          ผู้รายงาน: item.user_firstname,
          จังหวัด: item.province_name,
          ภูมิภาค: item.region_name,
          จำนวนตำแหน่ง: item.college_manpower_reportcountpos,
          สถานะ: this.getStatusText(item.college_manpower_reportfilestatus),
          วันที่รายงาน: this.formatDate(item.college_manpower_reporttimestamp)
        }));

        // Convert to CSV
        const csvContent = this.convertToCSV(csvData);

        // Download file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;"
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `college_report_${new Date().toISOString().split("T")[0]}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        swal.fire({
          title: "ส่งออกข้อมูลเรียบร้อย",
          icon: "success",
          showConfirmButton: false,
          timer: 1500
        });
      } catch (error) {
        swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถส่งออกข้อมูลได้"
        });
      } finally {
        this.exportLoading = false;
      }
    },

    convertToCSV(data) {
      if (!data || data.length === 0) return "";

      const headers = Object.keys(data[0]);
      const csvHeaders = headers.join(",");

      const csvRows = data.map(row =>
        headers
          .map(header => {
            const value = row[header];
            return `"${value}"`;
          })
          .join(",")
      );

      return [csvHeaders, ...csvRows].join("\n");
    },

    async periodQuery() {
      try {
        const result = await this.$http.post("period_college.php", {
          ApiKey: this.ApiKey,
          period_college_id: "801"
        });
        this.period_colleges = result.data;
      } catch (error) {
        console.error("Error fetching period data:", error);
      }
    },

    async college_manpower_reportQueryAll() {
      this.loading = true;

      try {
        const result = await this.$http.post("college_manpower_report.php", {
          ApiKey: this.ApiKey,
          college_manpower_reporttime: this.period_colleges
            .period_college_times,
          college_manpower_reportyear: this.period_colleges.period_college_year,
          college_manpower_reportype: "gov_em"
        });

        this.college_manpower_reports = result.data;
        this.calculateStatistics();
      } catch (error) {
        console.error("Error fetching college manpower report data:", error);
        swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลได้"
        });
      } finally {
        this.loading = false;
      }
    },

    calculateStatistics() {
      const data = this.college_manpower_reports;
      let countcc = 0;
      let countcf = 0;
      let countpc = 0;

      data.forEach(item => {
        switch (item.college_manpower_reportfilestatus) {
          case "cc":
            countcc++;
            break;
          case "cf":
            countcf++;
            break;
          default:
            countpc++;
            break;
        }
      });

      this.collegeReport = { countcc, countcf, countpc };
    },

    removeHeader(header) {
      this.selectedHeaders = this.selectedHeaders.filter(h => h !== header);
    },

    getStatusText(status) {
      switch (status) {
        case "cf":
          return "เสร็จสิ้น";
        case "cc":
          return "ผิดพลาด";
        default:
          return "รอดำเนินการ";
      }
    },

    getRegionColor(region) {
      const regionColorMap = {
        ภาคเหนือ: "blue",
        ภาคกลาง: "green",
        ภาคใต้: "orange",
        "ภาคตะวันออก กรุงเทพและปริมณฑล": "primary",
        ภาคตะวันออกเฉียงเหนือ: "red"
      };
      return regionColorMap[region] || "grey";
    },

    // ฟังก์ชันช่วยเหลือสำหรับจัดการข้อมูลภูมิภาค
    getRegionStats() {
      const regionStats = {};

      this.college_manpower_reports.forEach(item => {
        const regionName = item.region_name || "ไม่ระบุ";

        if (!regionStats[regionName]) {
          regionStats[regionName] = {
            count: 0,
            provinces: new Set(),
            colleges: new Set(),
            totalPositions: 0
          };
        }

        regionStats[regionName].count += 1;
        regionStats[regionName].provinces.add(item.province_name || "ไม่ระบุ");
        regionStats[regionName].colleges.add(
          item.college_manpower_reportcollege || "ไม่ระบุ"
        );
        regionStats[regionName].totalPositions += parseInt(
          item.college_manpower_reportcountpos || 0
        );
      });

      // แปลง Set เป็น Array length
      Object.keys(regionStats).forEach(region => {
        regionStats[region].provinces = regionStats[region].provinces.size;
        regionStats[region].colleges = regionStats[region].colleges.size;
      });

      return regionStats;
    },
    async cancelAlert(reportref) {
      const result = await swal.fire({
        title: "ขอยกเลิกการแนบไฟล์",
        text: "เนื่องจากแนบไฟล์ผิดพลาด",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b3151b",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      if (result.isConfirmed) {
        this.cancelLoading = reportref;

        try {
          const reportResult = await this.$http.post(
            "college_manpower_report.php",
            {
              ApiKey: this.ApiKey,
              college_manpower_reportref: reportref
            }
          );

          const reportData = reportResult.data;
          reportData.ApiKey = this.ApiKey;

          // Delete file
          await this.$http.post("deletefile.php", {
            ApiKey: this.ApiKey,
            filename: `../HRvecfiles/${reportData.college_manpower_reportfile}`
          });

          // Update record
          reportData.college_manpower_reportfile = "-";
          reportData.college_manpower_reportfilestatus = "-";

          await this.$http.post(
            "college_manpower_report.update.php",
            reportData
          );

          swal.fire({
            title: "ดำเนินการข้อมูลเรียบร้อย",
            icon: "success",
            showConfirmButton: false,
            timer: 1500
          });

          await this.college_manpower_reportQueryAll();
        } catch (error) {
          swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: error.message
          });
        } finally {
          this.deleteLoading = null;
        }
      }
    },

    getRegionDisplayName(region) {
      const regionNames = {
        ภาคเหนือ: "เหนือ",
        ภาคกลาง: "กลาง",
        ภาคใต้: "ใต้",
        "ภาคตะวันออก กรุงเทพและปริมณฑล": "ตะวันออก",
        ภาคตะวันออกเฉียงเหนือ: "อีสาน"
      };
      return regionNames[region] || region || "ไม่ระบุ";
    },

    getCountColor(count) {
      if (count >= 10) return "red";
      if (count >= 5) return "orange";
      return "green";
    },

    formatDate(timestamp) {
      if (!timestamp) return "-";
      return this.$moment(timestamp)
        .add(543, "year")
        .format("DD/MM/YYYY");
    },

    formatTime(timestamp) {
      if (!timestamp) return "-";
      return this.$moment(timestamp).format("HH:mm:ss");
    }
  }
};
</script>

<style scoped>
/* Statistics Cards */
.stat-card {
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2) !important;
}

.stat-card--active {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2) !important;
  border: 3px solid rgba(255, 255, 255, 0.8);
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s;
}

.stat-card:hover::before {
  left: 100%;
}

.stat-icon-container {
  position: relative;
  z-index: 1;
}

/* Chart Cards */
.chart-card {
  transition: all 0.3s ease;
  border-radius: 12px;
}

.chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Search Field */
.search-field {
  border-radius: 8px;
}

.search-field .v-input__slot {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.search-field .v-input__slot:hover {
  border-color: #455a64;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
}

/* Export Button */
.export-btn {
  background: linear-gradient(135deg, #455a64, #455a64);
  color: white;
  border: none;
  text-transform: none;
  font-weight: 500;
}

.export-btn:hover {
  background: linear-gradient(135deg, #455a64, #455a64);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

/* Modern Table */
.modern-table {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.modern-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-table >>> .v-data-table-header th {
  color: #495057 !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  transform: scale(1.01);
}

/* Column Styling */
.college-name {
  min-width: 150px;
}

.location-info {
  min-width: 120px;
}

.timestamp-col {
  min-width: 100px;
}

/* PDF Button */
.pdf-btn {
  transition: all 0.3s ease;
}

.pdf-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

/* Delete Button */
.delete-btn {
  transition: all 0.3s ease;
}

.delete-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }

  .chart-card {
    margin-bottom: 1rem;
  }

  .modern-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }
}

/* Loading States */
.v-progress-circular {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Dialog Enhancements */
.v-dialog {
  border-radius: 12px;
}

.v-dialog .v-card {
  border-radius: 12px;
}

/* Expansion Panel */
.v-expansion-panel {
  border-radius: 8px;
  overflow: hidden;
}

.v-expansion-panel-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  font-weight: 500;
}

/* Alert Enhancements */
.v-alert {
  border-radius: 8px;
  border-left: 4px solid;
}

/* Tooltip */
.v-tooltip__content {
  background: rgba(0, 0, 0, 0.9) !important;
  border-radius: 6px;
  font-size: 12px;
  padding: 8px 12px;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Animation Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Print Styles */
@media print {
  .v-btn,
  .v-dialog,
  .v-overlay {
    display: none !important;
  }

  .modern-table {
    box-shadow: none !important;
  }
}
</style>
